import { Tooltip } from "@mantine/core";
import { DateTime } from "luxon";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import { useEffect, useState } from "react";
import { DateInput } from "../../../../mantine/dates/DateInput/DateInput";
import { backendDateOrDateTimeToLuxonDateTimeOrNull } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTimeOrNull";
import { luxonDateTimeToBackendDateOrDateTimeOrNull } from "../../../../utils/dates/luxonDateTimeToBackendDateOrDateTimeOrNull";
import { getNeedsGebotstermin } from "../../../../utils/getNeedsGebotstermin";
import type {
  ExpressOnboardingEegColumnProps,
  ExpressOnboardingGenerator
} from "../../ExpressOnboarding.types";
import { useExpressOnboardingEegSave } from "../hooks/useExpressOnboardingEegSave";

function getExpressOnboardingTableGebotsterminColumn({
  getError,
  setError,
  getDraft,
  updateDraft,
  clearDraft,
  clearError
}: ExpressOnboardingEegColumnProps): MRT_ColumnDef<ExpressOnboardingGenerator> {
  return {
    id: "gebotstermin",
    accessorKey: "gebotstermin",
    header: "Gebotstermin",
    enableGlobalFilter: false,
    Cell: ({ cell, row }) => {
      const backendValue =
        cell.getValue<ExpressOnboardingGenerator["gebotstermin"]>();
      const value = backendDateOrDateTimeToLuxonDateTimeOrNull(backendValue);
      const rowDraft = getDraft(row.original.id);
      const draftOrValue =
        rowDraft && "gebotstermin" in rowDraft
          ? rowDraft.gebotstermin
          : backendValue;
      const jahrDesGebotstermins =
        rowDraft && "jahr_des_gebotstermins" in rowDraft
          ? rowDraft.jahr_des_gebotstermins
          : row.original.jahr_des_gebotstermins;
      const usesGebotstermin =
        !!jahrDesGebotstermins && getNeedsGebotstermin(jahrDesGebotstermins);

      /* local state is required for refreshes by other cells (foerderregime, jahr_des_gebotstermins) */
      const [currentValue, setCurrentValue] = useState<DateTime | null>(() =>
        backendDateOrDateTimeToLuxonDateTimeOrNull(draftOrValue)
      );

      useEffect(() => {
        setCurrentValue(
          backendDateOrDateTimeToLuxonDateTimeOrNull(draftOrValue)
        );
      }, [draftOrValue]);

      const { save, rowIsSaving } = useExpressOnboardingEegSave(row.original, {
        getError,
        setError,
        getDraft,
        updateDraft,
        clearDraft,
        clearError
      });

      const cantEditReason = row.original.uneditable_fields?.gebotstermin;

      return (
        <Tooltip
          disabled={!cantEditReason && usesGebotstermin}
          label={
            cantEditReason ??
            "Ein Gebotstermin muss nur für Gebote im Jahr 2025 hinterlegt werden."
          }
        >
          <DateInput
            disabled={!!cantEditReason || !usesGebotstermin || rowIsSaving}
            error={getError(row.original.id, "gebotstermin")}
            loading={rowIsSaving}
            maxDate={
              jahrDesGebotstermins
                ? DateTime.fromObject({ year: jahrDesGebotstermins }).endOf(
                    "year"
                  )
                : undefined
            }
            minDate={
              jahrDesGebotstermins
                ? DateTime.fromObject({
                    year: jahrDesGebotstermins
                  }).startOf("year")
                : undefined
            }
            value={currentValue}
            onBlur={() => {
              const newBackendValue =
                luxonDateTimeToBackendDateOrDateTimeOrNull(currentValue);
              const hasChange =
                newBackendValue !==
                luxonDateTimeToBackendDateOrDateTimeOrNull(value);
              if (!hasChange) {
                return;
              }

              updateDraft(row.original.id, { gebotstermin: newBackendValue });

              save({ gebotstermin: newBackendValue });
            }}
            onChange={(newDate) => {
              setCurrentValue(newDate);
              if (newDate) {
                clearError(row.original.id, "gebotstermin");
              }
            }}
          />
        </Tooltip>
      );
    }
  };
}

export { getExpressOnboardingTableGebotsterminColumn };
